import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import dayjs, { Dayjs } from 'dayjs'

import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import PageUrls from '../../../../constants/page-urls'
import { Box, Card, Grid, Typography } from '@mui/material'
import DateDistributionTable from './components/DateDistributionTable'
import IndividualDisplayItemsTable from './components/IndividualDisplayItemsTable'
import useAxios from '../../../../hooks/useAxios'
import { IProductDailyReport } from '../../../../types/report'
import DailyOrdersReportFilter, {
  IFilterMapped,
} from './components/DailyOrdersReportFilter'
import CircularLoading from '../../../../components/CircularProgress'
import * as XLSX from 'xlsx'
import { message } from 'antd'
import ExportExcel from '../../../../components/Export'
import { EOrderStatus, IOrder } from '../../../../types/order'
import OrdersTable from './components/OrdersTable'
import { PaginationResponseInterface } from '../../../../types/pagination'
import { numberFormat } from '../../../../utils'

const Menus = [
  {
    breadcrumbName: 'รายงานยอดขาย',
    title: 'รายงานยอดขาย',
    path: PageUrls.Report.DailySales,
  },
]

const DailyOrdersReportPage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)

  const [orderFilters, setOrderFilters] = useState({
    page: 1,
    perPage: 15,
  })

  const tableRef = useRef(null)

  const [searchFilter, setSearchFilter] = useState<IFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(1, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    commissionUsername: queryStr.commissionUsername as string | undefined,
  })
  const [filter, setFilter] = useState<IFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(1, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    trackingStatus: queryStr.trackingStatus,
    commissionUsername: queryStr.commissionUsername as string | undefined,
  })

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.Report.DailyOrders}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(filter)
  }, [])

  const productDailyReports = useAxios<IProductDailyReport>({
    method: 'GET',
    url: '/report/product-daily-reports',
    params: {
      startDate: searchFilter.startDate,
      endDate: searchFilter.endDate,
      distribution: searchFilter.distributionObjectIds,
      products: searchFilter.productObjectIds,
      productGroups: searchFilter.productGroups,
      categories: searchFilter.categoryObjectIds,
      commissionUsername: searchFilter.commissionUsername ?? '',
      dateType: searchFilter.dateType ?? 'orderDate',
      trackingStatus:
        searchFilter.trackingStatus !== 'all'
          ? searchFilter.trackingStatus
          : '',
    },
  })

  const orders = useAxios<PaginationResponseInterface<IOrder>>({
    method: 'GET',
    url: '/order',
    params: {
      ...orderFilters,
      exceptStatus: [EOrderStatus.CANCLELLED, EOrderStatus.RETURNED],
      productIds: searchFilter.productObjectIds,
      productGroups: searchFilter.productGroups,
      categoryIds: searchFilter.categoryObjectIds,
      distributionChannels: searchFilter.distributionObjectIds,
      startDate: searchFilter.startDate,
      endDate: searchFilter.endDate,
      dateType: searchFilter.dateType ?? 'orderDate',
      comission: searchFilter.commissionUsername ?? '',
      statusOrderShowTracking:
        searchFilter.trackingStatus !== 'all'
          ? searchFilter.trackingStatus
          : '',
    },
  })

  const handleOnDownload = useCallback(async (date: Dayjs) => {
    if (tableRef.current) {
      const ws = XLSX.utils.table_to_book(tableRef.current)
      const excelBuffer = XLSX.write(ws, { bookType: 'xlsx', type: 'array' })
      const data = new Blob([excelBuffer], { type: 'application/octet-stream' })

      const url = window.URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      link.download = `orderDailyReport.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
      link.remove()
      message.success('ดาวน์โหลดสำเร็จ')
    } else {
      message.error('ไม่พบข้อมูลสำหรับการดาวน์โหลด')
    }
  }, [])

  return (
    <ContentWrapper>
      <Helmet title="รายงานการสั่งของ" />
      <CircularLoading isLoading={productDailyReports.isLoading} />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="รายงานการสั่งของ"
            routes={Menus}
            descriptions="รายงานการสั่งของ"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={12}>
          <Card
            sx={{
              padding: 2,
            }}
          >
            <DailyOrdersReportFilter
              filter={filter}
              onFilterChange={onFilterChange}
              onSearch={handleSearch}
            />
          </Card>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Card sx={{ padding: 2 }}>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ marginBottom: 1 }}
            >
              รายการสั่งสินค้าแยกหมวดหมู่รายวัน
            </Typography>
            <DateDistributionTable data={productDailyReports.data?.category} />
          </Card>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Card sx={{ padding: 2 }}>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ marginBottom: 1 }}
            >
              รายการสั่งสินค้ารายวัน
            </Typography>
            <DateDistributionTable
              type="product"
              data={productDailyReports.data?.products}
            />
          </Card>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Card sx={{ padding: 2 }}>
            <Grid
              container
              spacing={2}
              alignItems={'center'}
              mb={2}
              justifyContent={'space-between'}
            >
              <Grid item xs={12} lg={6}>
                <Typography variant="h5" mb={0} color="text.secondary">
                  รายการรวมสินค้าทั้งหมด
                </Typography>
              </Grid>
              <Grid item xs={12} lg={2}>
                <Box textAlign={'right'}>
                  <ExportExcel
                    onDownload={handleOnDownload}
                    filterDate={false}
                  />
                </Box>
              </Grid>
            </Grid>
            <IndividualDisplayItemsTable
              data={productDailyReports.data?.total}
              ref={tableRef}
            />
          </Card>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Card sx={{ padding: 2 }}>
            <Typography variant="h5" mb={2} color="text.secondary">
              รายละเอียดคำสั่งซื้อ ({numberFormat(orders.data?.count ?? 0)}{' '}
              รายการ)
            </Typography>
            <OrdersTable
              setOrderFilters={setOrderFilters}
              total={orders.data?.count ?? 0}
              page={orderFilters.page}
              perPage={orderFilters.perPage}
              orders={orders.data?.records ?? []}
            />
          </Card>
        </Grid>
      </Grid>
    </ContentWrapper>
  )
}

export default DailyOrdersReportPage
